import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  Typography,
  Box,
  Autocomplete,
  TextField,
  CircularProgress,
  IconButton,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import { LoadingButton } from "@mui/lab";
import { useQuery } from "@apollo/client";
import { GET_STAFF_DETAILS } from "../apis/organization-apis/people-apis/peopleApis";

const getInitials = (name, email) => {
  if (name) {
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2)
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  if (email) return email[0].toUpperCase();
  return "?";
};
const UNASSIGN_OPTION = {
  id: null,
  name: "Unassign",
  email: "",
  jobTitle: "",
  isAdmin: false,
  isUnassignOption: true,
};

/**
 * Generic user-selection dialog.
 *
 * Props:
 *  open           – boolean
 *  onClose        – () => void
 *  onConfirm      – (user: { id, name, email, jobTitle, isAdmin }) => void | Promise
 *                   Called when the user clicks Save. The parent is responsible
 *                   for the mutation / API call and should pass `saving` accordingly.
 *  saving         – boolean  loading state for the Save button (controlled by parent)
 *  title          – string   dialog heading  (default: "Select User")
 *  description    – string   subtitle text   (optional)
 *  label          – string   autocomplete field label (default: "Select user")
 *  currentUser    – { name?, email? }  pre-selects the matching staff member
 *  saveLabel      – string   Save button text (default: "Save")
 *  adminsOnly     – boolean  show only admins in selector (default: false)
 *  showUnassignOption – boolean show unassign in options (default: false)
 */
const UserSelectionDialog = ({
  open,
  onClose,
  onConfirm,
  saving = false,
  title = "Select User",
  description,
  label = "Select user",
  currentUser = null,
  saveLabel = "Save",
  adminsOnly = false,
  showUnassignOption = false,
}) => {
  const theme = useTheme();
  const [selectedUser, setSelectedUser] = useState(null);

  const { data: staffData, loading: staffLoading } = useQuery(GET_STAFF_DETAILS, {
    skip: !open,
    fetchPolicy: "cache-first",
  });

  const staffOptions = useMemo(() => {
    if (!staffData?.getEmployees?.data) return [];
    return staffData.getEmployees.data
      .filter((e) => e.in_scope !== false)
      .map((e) => ({
        id: e.employee_data_id,
        name: e.name || `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim(),
        email:
          e.work_email ||
          e.emails?.find((em) => em.type === "work")?.email ||
          "",
        jobTitle: e.employments?.[0]?.job_title || "",
        isAdmin: e.roles?.some((r) => r.role_description === "admin"),
      }))
      .filter((e) => (adminsOnly ? e.isAdmin : true))
      .sort((a, b) => (b.isAdmin ? 1 : 0) - (a.isAdmin ? 1 : 0));
  }, [staffData, adminsOnly]);

  useEffect(() => {
    if (!open) {
      setSelectedUser(null);
      return;
    }
    if (currentUser && staffOptions.length) {
      const match = staffOptions.find(
        (s) => s.email === currentUser.email || s.name === currentUser.name
      );
      setSelectedUser(match ?? null);
    }
  }, [open, currentUser, staffOptions]);

  const handleSave = () => {
    if (!selectedUser) return;
    if (selectedUser.isUnassignOption) {
      onConfirm?.(null);
      return;
    }
    onConfirm?.(selectedUser);
  };
  const selectableOptions = useMemo(
    () => (showUnassignOption ? [UNASSIGN_OPTION, ...staffOptions] : staffOptions),
    [showUnassignOption, staffOptions]
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontWeight: 600,
          fontSize: "1.15rem",
          color: theme.customColors.text.strong,
          background: theme.customColors.background.cardHeader,
          borderBottom: `1px solid ${theme.customColors.border.main}`,
          pb: 1.5,
          pt: 2,
          px: 3,
          position: "relative",
        }}
      >
        {title}
        <IconButton
          onClick={onClose}
          size="small"
          aria-label="Close"
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            color: "grey.500",
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          px: 3,
          py: 3,
          "&.MuiDialogContent-root": {
            paddingTop: 3,
          },
        }}
      >
        {description && (
          <Typography
            variant="body2"
            sx={{
              color: theme.customColors.text.secondary,
              lineHeight: 1.6,
              fontSize: "0.9375rem",
              mb: 2.5,
            }}
          >
            {description}
          </Typography>
        )}

        <Autocomplete
          options={selectableOptions}
          loading={staffLoading}
          value={selectedUser}
          onChange={(_, val) => setSelectedUser(val)}
          getOptionLabel={(o) =>
            o.isUnassignOption ? "Unassign" : o.name || o.email || ""
          }
          groupBy={
            adminsOnly
              ? undefined
              : (o) =>
                  o.isUnassignOption ? "Actions" : o.isAdmin ? "Admins" : "Staff"
          }
          isOptionEqualToValue={(o, v) => o.id === v?.id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder="Search..."
              size="small"
              sx={{
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                {
                  borderColor: theme.palette.primary.main,
                },
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {staffLoading && (
                      <CircularProgress size={14} sx={{ mr: 1 }} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...rest } = props;
            if (option.isUnassignOption) {
              return (
                <Box
                  key={key}
                  component="li"
                  {...rest}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    py: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      backgroundColor: "#EEF2FF",
                      color: "#4F46E5",
                      flexShrink: 0,
                    }}
                  >
                    <PersonOffOutlinedIcon sx={{ fontSize: 18 }} />
                  </Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={500} noWrap>
                      Unassign
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      Remove current owner
                    </Typography>
                  </Box>
                </Box>
              );
            }
            const initials = getInitials(option.name, option.email);
            return (
              <Box
                key={key}
                component="li"
                {...rest}
                sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    backgroundColor: "#1976d2",
                    color: "#fff",
                    flexShrink: 0,
                  }}
                >
                  {initials}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>
                    {option.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {option.isAdmin ? "Admin" : option.jobTitle || option.email}
                  </Typography>
                </Box>
              </Box>
            );
          }}
          renderGroup={(params) => (
            <Box key={params.key}>
              <Typography
                variant="caption"
                fontWeight={700}
                color="text.secondary"
                sx={{
                  px: 2,
                  py: 0.5,
                  display: "block",
                  backgroundColor: "#F7F7FC",
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                }}
              >
                {params.group}
              </Typography>
              {params.children}
            </Box>
          )}
        />
      </DialogContent>

      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          px: 3,
          pt: 1,
          pb: 1.25,
          gap: 1,
          minHeight: 0,
          background: theme.customColors.background.cardHeader,
          borderTop: `1px solid ${theme.customColors.border.main}`,
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          disabled={saving}
          size="small"
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            borderColor: theme.customColors.border.main,
            color: theme.customColors.text.strong,
            "&:hover": {
              borderColor: theme.customColors.border.strong,
              backgroundColor: theme.customColors.background.hover,
              color: theme.customColors.text.strong,
            },
          }}
        >
          Cancel
        </Button>
        <LoadingButton
          loading={saving}
          onClick={handleSave}
          variant="contained"
          size="small"
          disabled={!selectedUser}
          sx={{
            fontWeight: 500,
            fontSize: "0.875rem",
            px: 2.5,
            py: 0.875,
            borderRadius: 1,
            color: theme.customColors.button.text,
            "&:hover": {
              bgcolor: theme.customColors.button.primary,
            },
            "&:disabled": {
              bgcolor: theme.customColors.background.light,
              color: theme.customColors.text.disabled,
            },
          }}
        >
          {saveLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default UserSelectionDialog;
